/**
 * Portal Kit: o formulário de cada uma das nove colunas.
 *
 * O ENDEREÇO (`base`) de tempo, agro, mercado, horóscopo e futebol NÃO é editado
 * aqui: mora na aba "Rotas e SEO", onde se vê o conjunto de endereços junto e a
 * regra de que nenhum pode brigar com outro. Aqui ficam as listas, os limiares,
 * os textos e os caches.
 */
import {
  PAGINAS_FUTEBOL,
  CARTOES_HOME,
  type CatalogosPortal,
  type ConfigAgro,
  type ConfigFonte,
  type ConfigFutebol,
  type ConfigHoroscopo,
  type ConfigLoterias,
  type ConfigMapa,
  type ConfigMercado,
  type ConfigServicos,
  type ConfigTempo,
  type ErroDeCampo,
  type IdSerie,
  type PaginaFutebol,
  type SerieFutebol,
  type Signo,
  type SlugSigno,
  type TextosPagina,
} from '@/types/portal';

import { CampoInterruptor, CampoNumero, CampoSelecao, CampoTexto } from './campos';
import { EditorAtivos } from './EditorAtivos';
import { EditorFaixa } from './EditorFaixa';
import { EditorLimiares } from './EditorLimiares';
import { EditorLista } from './EditorLista';
import { EditorLocais } from './EditorLocais';
import { EditorModalidades } from './EditorModalidades';
import { EditorSelecao } from './EditorSelecao';
import { erroDe } from './helpers';
import styles from './Portal.module.css';

interface Ctx<C> {
  config: C;
  mudar: (c: C) => void;
  erros: ErroDeCampo[];
}

// ─── textos das páginas (tempo, agro, horóscopo) ─────────────────────────────

function EditorTextos({
  textos,
  onChange,
  erros,
}: {
  textos: TextosPagina;
  onChange: (t: TextosPagina) => void;
  erros: ErroDeCampo[];
}) {
  return (
    <div>
      <h4 className={styles.sub2}>Textos das páginas</h4>
      <p className={styles.dica}>
        Marcadores aceitos: {'{n}'} (quantidade), {'{nome}'}, {'{uf}'} e {'{periodo}'}. Marcador sem
        valor na página é removido.
      </p>
      <div className={styles.grade2}>
        <CampoTexto
          rotulo="Título do índice"
          valor={textos.indice_titulo}
          erro={erroDe(erros, 'config.textos.indice_titulo')}
          onChange={(indice_titulo) => onChange({ ...textos, indice_titulo })}
        />
        <CampoTexto
          rotulo="Descrição do índice"
          valor={textos.indice_descricao}
          erro={erroDe(erros, 'config.textos.indice_descricao')}
          onChange={(indice_descricao) => onChange({ ...textos, indice_descricao })}
        />
        <CampoTexto
          rotulo="Título de cada página"
          valor={textos.item_titulo}
          erro={erroDe(erros, 'config.textos.item_titulo')}
          onChange={(item_titulo) => onChange({ ...textos, item_titulo })}
        />
        <CampoTexto
          rotulo="Descrição de cada página"
          valor={textos.item_descricao}
          erro={erroDe(erros, 'config.textos.item_descricao')}
          onChange={(item_descricao) => onChange({ ...textos, item_descricao })}
        />
      </div>
    </div>
  );
}

// ─── tempo ───────────────────────────────────────────────────────────────────

export function EditorTempo({ config: c, mudar, erros }: Ctx<ConfigTempo>) {
  return (
    <>
      <div className={styles.grade}>
        <CampoTexto
          rotulo="Categoria das matérias de clima"
          valor={c.categoria}
          mono
          dica="Pelo slug da categoria no WordPress."
          erro={erroDe(erros, 'config.categoria')}
          onChange={(categoria) => mudar({ ...c, categoria })}
        />
        <CampoNumero
          rotulo="Dias de previsão"
          valor={c.dias_previsao}
          min={1}
          max={16}
          erro={erroDe(erros, 'config.dias_previsao')}
          onChange={(dias_previsao) => mudar({ ...c, dias_previsao })}
        />
        <CampoNumero
          rotulo="Validade do cache (s)"
          valor={c.cache_s}
          min={30}
          max={86_400}
          erro={erroDe(erros, 'config.cache_s')}
          onChange={(cache_s) => mudar({ ...c, cache_s })}
        />
      </div>
      <EditorLocais
        rotulo="Cidades"
        singular="cidade"
        locais={c.cidades}
        campo="config.cidades"
        erros={erros}
        onChange={(cidades) => mudar({ ...c, cidades })}
      />
      <EditorTextos
        textos={c.textos}
        erros={erros}
        onChange={(textos) => mudar({ ...c, textos })}
      />
    </>
  );
}

// ─── mapa ────────────────────────────────────────────────────────────────────

export function EditorMapa({ config: c, mudar, erros }: Ctx<ConfigMapa>) {
  return (
    <>
      <p className={styles.dica}>
        O mapa usa as cidades da coluna "Previsão do tempo" como marcadores; por isso exige essa
        coluna ativa.
      </p>
      <div className={styles.grade}>
        <CampoInterruptor
          rotulo="Mostrar avisos oficiais do INMET"
          valor={c.avisos_inmet}
          onChange={(avisos_inmet) => mudar({ ...c, avisos_inmet })}
        />
        <CampoInterruptor
          rotulo="Marcar capital com condição severa agora"
          valor={c.condicao_severa}
          onChange={(condicao_severa) => mudar({ ...c, condicao_severa })}
        />
        <CampoSelecao<'1' | '2' | '3'>
          rotulo="Grau mínimo do aviso"
          valor={String(c.grau_minimo) as '1' | '2' | '3'}
          opcoes={[
            { valor: '1', rotulo: '1 · perigo potencial' },
            { valor: '2', rotulo: '2 · perigo' },
            { valor: '3', rotulo: '3 · grande perigo' },
          ]}
          erro={erroDe(erros, 'config.grau_minimo')}
          onChange={(v) => mudar({ ...c, grau_minimo: Number(v) })}
        />
        <CampoNumero
          rotulo="Validade do cache dos avisos (s)"
          valor={c.cache_avisos_s}
          min={30}
          max={86_400}
          erro={erroDe(erros, 'config.cache_avisos_s')}
          onChange={(cache_avisos_s) => mudar({ ...c, cache_avisos_s })}
        />
      </div>
    </>
  );
}

// ─── agro ────────────────────────────────────────────────────────────────────

export function EditorAgro({ config: c, mudar, erros }: Ctx<ConfigAgro>) {
  return (
    <>
      <div className={styles.grade}>
        <CampoNumero
          rotulo="Dias de previsão"
          valor={c.dias_previsao}
          min={1}
          max={16}
          erro={erroDe(erros, 'config.dias_previsao')}
          onChange={(dias_previsao) => mudar({ ...c, dias_previsao })}
        />
        <CampoNumero
          rotulo="Dias passados"
          valor={c.dias_passados}
          min={0}
          max={92}
          erro={erroDe(erros, 'config.dias_passados')}
          onChange={(dias_passados) => mudar({ ...c, dias_passados })}
        />
        <CampoNumero
          rotulo="Validade do cache (s)"
          valor={c.cache_s}
          min={30}
          max={86_400}
          erro={erroDe(erros, 'config.cache_s')}
          onChange={(cache_s) => mudar({ ...c, cache_s })}
        />
      </div>
      <EditorLocais
        rotulo="Praças"
        singular="praça"
        locais={c.pracas}
        campo="config.pracas"
        erros={erros}
        onChange={(pracas) => mudar({ ...c, pracas })}
      />
      <EditorLimiares
        limiares={c.limiares}
        campo="config.limiares"
        erros={erros}
        onChange={(limiares) => mudar({ ...c, limiares })}
      />
      <EditorTextos
        textos={c.textos}
        erros={erros}
        onChange={(textos) => mudar({ ...c, textos })}
      />
    </>
  );
}

// ─── mercado ─────────────────────────────────────────────────────────────────

export function EditorMercado({ config: c, mudar, erros }: Ctx<ConfigMercado>) {
  const rotulosDoTicker = c.ticker.map((a) => a.rotulo).filter((r) => r.trim() !== '');
  const opcoesPainel = [...new Set([...rotulosDoTicker, ...c.painel_materia])].map((r) => ({
    valor: r,
    rotulo: r,
  }));
  return (
    <>
      <div className={styles.grade2}>
        <CampoTexto
          rotulo="Título da página"
          valor={c.titulo}
          erro={erroDe(erros, 'config.titulo')}
          onChange={(titulo) => mudar({ ...c, titulo })}
        />
        <CampoTexto
          rotulo="Descrição da página"
          valor={c.descricao}
          erro={erroDe(erros, 'config.descricao')}
          onChange={(descricao) => mudar({ ...c, descricao })}
        />
      </div>
      <EditorAtivos
        ativos={c.ticker}
        campo="config.ticker"
        erros={erros}
        onChange={(ticker) => mudar({ ...c, ticker })}
      />
      <EditorSelecao
        rotulo="Painel das matérias diárias"
        dica="Cada rótulo precisa existir no ticker."
        opcoes={opcoesPainel}
        selecionados={c.painel_materia}
        erro={erroDe(erros, 'config.painel_materia')}
        onChange={(painel_materia) => mudar({ ...c, painel_materia })}
      />
      <div className={styles.grade}>
        <CampoNumero
          rotulo="Cache do painel (s)"
          valor={c.cache.painel_s}
          min={30}
          max={86_400}
          erro={erroDe(erros, 'config.cache.painel_s')}
          onChange={(painel_s) => mudar({ ...c, cache: { ...c.cache, painel_s } })}
        />
        <CampoNumero
          rotulo="Cache de cada ativo (s)"
          valor={c.cache.ativo_s}
          min={30}
          max={86_400}
          erro={erroDe(erros, 'config.cache.ativo_s')}
          onChange={(ativo_s) => mudar({ ...c, cache: { ...c.cache, ativo_s } })}
        />
        <CampoNumero
          rotulo="Cache dos indicadores (s)"
          valor={c.cache.indicadores_s}
          min={30}
          max={86_400}
          erro={erroDe(erros, 'config.cache.indicadores_s')}
          onChange={(indicadores_s) => mudar({ ...c, cache: { ...c.cache, indicadores_s } })}
        />
        <CampoNumero
          rotulo="Atraso declarado das cotações (min)"
          valor={c.atraso_declarado_min}
          min={1}
          max={1440}
          dica="É o atraso que o rodapé do bloco informa ao leitor."
          erro={erroDe(erros, 'config.atraso_declarado_min')}
          onChange={(atraso_declarado_min) => mudar({ ...c, atraso_declarado_min })}
        />
        <CampoNumero
          rotulo="Idade máxima do painel (s)"
          valor={c.idade_maxima_s}
          min={60}
          max={86_400}
          dica="Mais velho que isto, o painel deixa de ser mostrado no lugar do dado."
          erro={erroDe(erros, 'config.idade_maxima_s')}
          onChange={(idade_maxima_s) => mudar({ ...c, idade_maxima_s })}
        />
        <CampoNumero
          rotulo="Manchetes na página"
          valor={c.manchetes_limite}
          min={0}
          max={30}
          erro={erroDe(erros, 'config.manchetes_limite')}
          onChange={(manchetes_limite) => mudar({ ...c, manchetes_limite })}
        />
        <CampoTexto
          rotulo="Prefixo do endereço das matérias diárias"
          valor={c.prefixo_slug_diaria}
          mono
          erro={erroDe(erros, 'config.prefixo_slug_diaria')}
          onChange={(prefixo_slug_diaria) => mudar({ ...c, prefixo_slug_diaria })}
        />
        <CampoInterruptor
          rotulo="Mostrar indicadores do Banco Central"
          valor={c.indicadores_bcb}
          onChange={(indicadores_bcb) => mudar({ ...c, indicadores_bcb })}
        />
      </div>
    </>
  );
}

// ─── horóscopo ───────────────────────────────────────────────────────────────

export function EditorHoroscopo({
  config: c,
  mudar,
  erros,
  catalogos,
}: Ctx<ConfigHoroscopo> & { catalogos: CatalogosPortal }) {
  // O catálogo do produto traz os 12; se faltar algum (servidor antigo), vale o que a config já tem.
  const todos: Signo[] = [...catalogos.signos];
  for (const s of c.signos) if (!todos.some((t) => t.slug === s.slug)) todos.push(s);
  const mudarSelecao = (slugs: SlugSigno[]) =>
    mudar({
      ...c,
      signos: slugs
        .map((slug) => c.signos.find((s) => s.slug === slug) ?? todos.find((s) => s.slug === slug))
        .filter((s): s is Signo => s !== undefined),
    });
  return (
    <>
      <div className={styles.grade}>
        <CampoTexto
          rotulo="Categoria"
          valor={c.categoria}
          mono
          erro={erroDe(erros, 'config.categoria')}
          onChange={(categoria) => mudar({ ...c, categoria })}
        />
        <CampoNumero
          rotulo="Prévia do texto (caracteres)"
          valor={c.previa_max_caracteres}
          min={80}
          max={600}
          erro={erroDe(erros, 'config.previa_max_caracteres')}
          onChange={(previa_max_caracteres) => mudar({ ...c, previa_max_caracteres })}
        />
        <CampoTexto
          rotulo="Página-mãe dos textos (slug)"
          valor={c.texto.pagina_pai_slug ?? ''}
          mono
          dica="Deixe vazio se o texto do dia vem só do post diário."
          erro={erroDe(erros, 'config.texto.pagina_pai_slug')}
          onChange={(v) =>
            mudar({ ...c, texto: { ...c.texto, pagina_pai_slug: v === '' ? null : v } })
          }
        />
        <CampoTexto
          rotulo="Prefixo do post diário do signo"
          valor={c.texto.prefixo_slug_post}
          mono
          dica="Termina com hífen; o signo vem depois."
          erro={erroDe(erros, 'config.texto.prefixo_slug_post')}
          onChange={(prefixo_slug_post) =>
            mudar({ ...c, texto: { ...c.texto, prefixo_slug_post } })
          }
        />
      </div>
      <EditorSelecao<SlugSigno>
        rotulo="Signos com página"
        dica="Fora da lista, a página do signo responde 404. A ordem é a da tela."
        ordenavel
        opcoes={todos.map((s) => ({ valor: s.slug, rotulo: s.nome, detalhe: s.periodo }))}
        selecionados={c.signos.map((s) => s.slug)}
        erro={erroDe(erros, 'config.signos')}
        onChange={mudarSelecao}
      />
      <EditorTextos
        textos={c.textos}
        erros={erros}
        onChange={(textos) => mudar({ ...c, textos })}
      />
    </>
  );
}

// ─── loterias ────────────────────────────────────────────────────────────────

export function EditorLoterias({ config: c, mudar, erros }: Ctx<ConfigLoterias>) {
  return (
    <>
      <div className={styles.grade}>
        <CampoTexto
          rotulo="Categoria"
          valor={c.categoria}
          mono
          erro={erroDe(erros, 'config.categoria')}
          onChange={(categoria) => mudar({ ...c, categoria })}
        />
        <CampoNumero
          rotulo="Outros resultados no quadro"
          valor={c.outros_resultados_max}
          min={1}
          max={20}
          erro={erroDe(erros, 'config.outros_resultados_max')}
          onChange={(outros_resultados_max) => mudar({ ...c, outros_resultados_max })}
        />
      </div>
      <EditorModalidades
        modalidades={c.modalidades}
        campo="config.modalidades"
        erros={erros}
        onChange={(modalidades) => mudar({ ...c, modalidades })}
      />
    </>
  );
}

// ─── futebol ─────────────────────────────────────────────────────────────────

const ROTULO_PAGINA: Record<PaginaFutebol, string> = {
  rodada: 'Rodada',
  estatisticas: 'Estatísticas',
  jogo: 'Jogo',
  clube: 'Clube',
  atleta: 'Atleta',
};

const TAMANHOS_COMUNS = [16, 24, 32, 48, 64, 96, 128, 192, 256, 384, 512];

export function EditorFutebol({
  config: c,
  mudar,
  erros,
  catalogos,
}: Ctx<ConfigFutebol> & { catalogos: CatalogosPortal }) {
  const todas: SerieFutebol[] = [...catalogos.series];
  for (const s of c.series) if (!todas.some((t) => t.id === s.id)) todas.push(s);
  const mudarSeries = (ids: IdSerie[]) =>
    mudar({
      ...c,
      series: ids
        .map((id) => c.series.find((s) => s.id === id) ?? todas.find((s) => s.id === id))
        .filter((s): s is SerieFutebol => s !== undefined),
    });
  const tamanhos = [...new Set([...TAMANHOS_COMUNS, ...c.imagens.tamanhos_px])].sort(
    (a, b) => a - b,
  );
  return (
    <>
      <div className={styles.grade}>
        <CampoTexto
          rotulo="Categoria de esporte"
          valor={c.categoria}
          mono
          erro={erroDe(erros, 'config.categoria')}
          onChange={(categoria) => mudar({ ...c, categoria })}
        />
        <CampoNumero
          rotulo="Alerta de atraso da tabela (min)"
          valor={c.atraso_alerta_min}
          min={1}
          max={1440}
          dica='Acima disto a tabela mostra o selo "sem mudança há N min".'
          erro={erroDe(erros, 'config.atraso_alerta_min')}
          onChange={(atraso_alerta_min) => mudar({ ...c, atraso_alerta_min })}
        />
        <CampoNumero
          rotulo="Cache da tabela (s)"
          valor={c.cache.tabela_s}
          min={30}
          max={86_400}
          erro={erroDe(erros, 'config.cache.tabela_s')}
          onChange={(tabela_s) => mudar({ ...c, cache: { ...c.cache, tabela_s } })}
        />
        <CampoNumero
          rotulo="Cache do detalhe (s)"
          valor={c.cache.detalhe_s}
          min={30}
          max={86_400}
          erro={erroDe(erros, 'config.cache.detalhe_s')}
          onChange={(detalhe_s) => mudar({ ...c, cache: { ...c.cache, detalhe_s } })}
        />
      </div>
      <EditorSelecao<IdSerie>
        rotulo="Séries com página"
        dica="Série fora da lista responde 404."
        ordenavel
        opcoes={todas.map((s) => ({ valor: s.id, rotulo: s.nome }))}
        selecionados={c.series.map((s) => s.id)}
        erro={erroDe(erros, 'config.series')}
        onChange={mudarSeries}
      />
      <EditorSelecao<PaginaFutebol>
        rotulo="Páginas de detalhe"
        dica="A página da série e o índice existem sempre que a coluna está ativa."
        opcoes={PAGINAS_FUTEBOL.map((p) => ({ valor: p, rotulo: ROTULO_PAGINA[p] }))}
        selecionados={c.paginas}
        onChange={(paginas) => mudar({ ...c, paginas })}
      />
      <div className={styles.grade}>
        <CampoSelecao<IdSerie>
          rotulo="Série do cartão da home"
          valor={c.home.serie}
          opcoes={c.series.map((s) => ({ valor: s.id, rotulo: s.nome }))}
          erro={erroDe(erros, 'config.home.serie')}
          onChange={(serie) => mudar({ ...c, home: { ...c.home, serie } })}
        />
        <CampoNumero
          rotulo="Linhas do cartão da home"
          valor={c.home.linhas}
          min={3}
          max={20}
          erro={erroDe(erros, 'config.home.linhas')}
          onChange={(linhas) => mudar({ ...c, home: { ...c.home, linhas } })}
        />
        <CampoInterruptor
          rotulo="Espelhar escudos e fotos no site"
          valor={c.imagens.espelho_local}
          dica="A página nunca aponta para o endereço do fornecedor."
          onChange={(espelho_local) => mudar({ ...c, imagens: { ...c.imagens, espelho_local } })}
        />
      </div>
      <EditorSelecao<string>
        rotulo="Tamanhos de imagem (px)"
        dica="De 1 a 4 tamanhos."
        opcoes={tamanhos.map((t) => ({ valor: String(t), rotulo: `${t} px` }))}
        selecionados={c.imagens.tamanhos_px.map(String)}
        erro={erroDe(erros, 'config.imagens.tamanhos_px')}
        onChange={(v) =>
          mudar({
            ...c,
            imagens: { ...c.imagens, tamanhos_px: v.map(Number).sort((a, b) => a - b) },
          })
        }
      />
    </>
  );
}

// ─── serviços ao vivo ────────────────────────────────────────────────────────

const ROTULO_CARTAO: Record<(typeof CARTOES_HOME)[number], string> = {
  tempo: 'Previsão do tempo',
  mercado: 'Mercado',
  horoscopo: 'Horóscopo',
  futebol: 'Futebol',
};

export function EditorServicos({ config: c, mudar, erros }: Ctx<ConfigServicos>) {
  return (
    <>
      <div className={styles.grade2}>
        <CampoTexto
          rotulo="Título da seção de serviços na home"
          valor={c.titulo_home}
          erro={erroDe(erros, 'config.titulo_home')}
          onChange={(titulo_home) => mudar({ ...c, titulo_home })}
        />
        <CampoInterruptor
          rotulo="Mostrar a barra de mercados (ticker) sob o cabeçalho"
          valor={c.ticker}
          dica="Exige a coluna Mercado ativa."
          onChange={(ticker) => mudar({ ...c, ticker })}
        />
      </div>
      <EditorSelecao<(typeof CARTOES_HOME)[number]>
        rotulo="Cartões da home"
        dica="Cada cartão exige a coluna dele ativa. A ordem é a da tela."
        ordenavel
        opcoes={CARTOES_HOME.map((k) => ({ valor: k, rotulo: ROTULO_CARTAO[k] }))}
        selecionados={c.cartoes_home}
        onChange={(cartoes_home) => mudar({ ...c, cartoes_home })}
      />
      <EditorFaixa
        itens={c.faixa}
        campo="config.faixa"
        erros={erros}
        onChange={(faixa) => mudar({ ...c, faixa })}
      />
    </>
  );
}

// ─── fonte das matérias ──────────────────────────────────────────────────────

export function EditorFonte({ config: c, mudar, erros }: Ctx<ConfigFonte>) {
  return (
    <>
      <div className={styles.grade}>
        <CampoInterruptor
          rotulo="Exigir o link da fonte nas matérias de notícia"
          valor={c.exigir_fonte}
          dica="Vale também para quem publica por fora do WIREDESK."
          onChange={(exigir_fonte) => mudar({ ...c, exigir_fonte })}
        />
        <CampoSelecao<'avisar' | 'rascunho'>
          rotulo="Matéria sem fonte"
          valor={c.acao_sem_fonte}
          opcoes={[
            { valor: 'avisar', rotulo: 'Publicar e registrar o aviso' },
            { valor: 'rascunho', rotulo: 'Voltar a rascunho com o motivo' },
          ]}
          dica="Trava nova nasce avisando; passe para rascunho quando a equipe já cita a fonte."
          onChange={(acao_sem_fonte) => mudar({ ...c, acao_sem_fonte })}
        />
        <CampoTexto
          rotulo="Rótulo da fonte"
          valor={c.rotulo}
          maxLength={30}
          dica='Singular; o plural é "Fontes".'
          erro={erroDe(erros, 'config.rotulo')}
          onChange={(rotulo) => mudar({ ...c, rotulo })}
        />
      </div>
      <EditorLista
        rotulo="Categorias isentas"
        item="Categoria"
        dica="Serviços de dado e colunas próprias, que já levam a fonte no próprio bloco."
        itens={c.categorias_isentas}
        campo="config.categorias_isentas"
        erros={erros}
        mono
        placeholder="mercado"
        max={40}
        onChange={(categorias_isentas) => mudar({ ...c, categorias_isentas })}
      />
      <EditorLista
        rotulo="Metas antigas de fonte"
        item="Meta"
        dica="Lidas só como reserva, para não reescrever matéria publicada. Nunca gravadas."
        itens={c.meta_legadas}
        campo="config.meta_legadas"
        erros={erros}
        mono
        max={8}
        onChange={(meta_legadas) => mudar({ ...c, meta_legadas })}
      />
    </>
  );
}
